import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { AppProvider, useAppContext, useAppActions } from './context/AppContext';
import { getAvailableTabs } from './utils/helpers';
import AuthService from './services/security/AuthService';
import SessionManager from './services/security/SessionManager';
import FacilityService from './services/api/FacilityService';
import Login from './components/Auth/Login';

// Import all components
import Dashboard from './components/Dashboard/Dashboard';
import Residents from './components/Residents/Residents';
import Family from './components/Residents/Family';
import Enquiries from './components/Enquiries/Enquiries';
import Clinical from './components/Clinical/Clinical';
import Medication from './components/Medication/Medication';
import CarePlans from './components/CarePlans/CarePlans';
import Telehealth from './components/Telehealth/Telehealth';
import Orders from './components/Orders/Orders';
import Quality from './components/Quality/Quality';
import Roster from './components/Roster/Roster';
import Billing from './components/Billing/Billing';
import Reports from './components/Reports/Reports';
import Network from './components/Network/Network';
import Settings from './components/Settings/Settings';
import Header from './components/Navigation/Header';
import Alerts from './components/Alerts/Alerts';
import Kitchen from './components/Kitchen/Kitchen';
import Maintenance from './components/Maintenance/Maintenance';
import Emergency from './components/Emergency/Emergency';
import Visitors from './components/Visitors/Visitors';
import Wellbeing from './components/Wellbeing/Wellbeing';
import FamilyPortal from './components/FamilyPortal/FamilyPortal';
import MobileCarerApp from './components/MobileCarerApp/MobileCarerApp';
import HRModule from './components/HR/HRModule';
import EnterpriseRoster from './components/HR/EnterpriseRoster';
import ErrorBoundary from './components/ErrorBoundary';
import { LoadingProvider } from './components/Common/LoadingProvider';
import { OfflineIndicator } from './components/Common/OfflineIndicator';
import { ToastProvider } from './components/Common/ToastProvider';
import { ConfirmProvider } from './components/Common/ConfirmDialog';

const MedFastCRMPro = () => {
  const { state } = useAppContext();
  const actions = useAppActions();
  const { 
    isAuthenticated,
    currentUser, 
    activeTab, 
    deviceInfo, 
    networkStatus, 
    notifications,
    selectedFacility
  } = state;
  const [facilities, setFacilities] = useState([]);
  const [facilitiesLoading, setFacilitiesLoading] = useState(isAuthenticated);
  const prevAuthenticatedRef = useRef(isAuthenticated);

  useLayoutEffect(() => {
    if (isAuthenticated) {
      if (!prevAuthenticatedRef.current) {
        setFacilitiesLoading(true);
      }
    } else {
      setFacilitiesLoading(false);
    }
    prevAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    const loadFacilities = async () => {
      try {
        const result = await FacilityService.getAll();
        if (result.success) {
          setFacilities(result.data || []);
          if (!selectedFacility && result.data && result.data.length > 0) {
            actions.setSelectedFacility(result.data[0].id);
          }
        }
      } catch (error) {
        console.error('Error loading facilities:', error);
      } finally {
        setFacilitiesLoading(false);
      }
    };
    loadFacilities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Idle timeout: listen for activity and expire session after 30 min
  useEffect(() => {
    if (!isAuthenticated) return;

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    const onActivity = () => SessionManager.recordActivity();
    activityEvents.forEach(ev => window.addEventListener(ev, onActivity, { passive: true }));

    SessionManager.onSessionExpired((reason) => {
      if (process.env.NODE_ENV === 'development') {
        console.debug('[Session] Expired:', reason);
      }
      actions.logout();
    });

    return () => {
      activityEvents.forEach(ev => window.removeEventListener(ev, onActivity));
    };
  }, [isAuthenticated, actions]);

  const handleLogin = useCallback(
    async (overrideCredentials) => {
      const { username, password } = overrideCredentials ?? state.loginCredentials;
      const result = await AuthService.login(username, password);

      if (result.success && !result.mfaRequired) {
        actions.login(result.user);
        actions.setLoginError('');
        if (!selectedFacility) {
          actions.setSelectedFacility('sunshine');
        }
        actions.setActiveTab('dashboard');
      } else if (result.success && result.mfaRequired) {
        actions.setLoginError('MFA verification required (enter code from authenticator app)');
      } else {
        const errorMsg = result.error || 'Invalid username or password';
        if (process.env.NODE_ENV === 'development') {
          console.debug('[Auth] Login failed:', errorMsg, { username });
        }
        actions.setLoginError(errorMsg);
      }
    },
    [state.loginCredentials, actions, selectedFacility]
  );

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  if (facilitiesLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white gap-3" role="status" aria-live="polite">
        <div className="h-10 w-10 rounded-full border-2 border-purple-200 border-t-purple-600 animate-spin" aria-hidden />
        <p className="text-sm text-gray-600">Loading facility data…</p>
      </div>
    );
  }

  // Render main application
  return (
    <div className="max-w-full mx-auto bg-white min-h-screen flex flex-col">
      {/* Header with Logo, Facility Selector, and Tabs */}
      <Header 
        currentUser={currentUser}
        networkStatus={networkStatus}
        notifications={notifications}
        deviceInfo={deviceInfo}
        onLogout={actions.logout}
        selectedFacility={selectedFacility || 'sunshine'}
        facilitiesList={facilities}
        onFacilityChange={actions.setSelectedFacility}
        activeTab={activeTab}
        availableTabs={getAvailableTabs(currentUser?.role)}
        onTabChange={actions.setActiveTab}
      />
      
      {/* Main Content */}
      <div className="p-4 flex-grow">
        {activeTab === 'dashboard' && <Dashboard />}
        {activeTab === 'residents' && <Residents />}
        {activeTab === 'family' && <Family />}
        {activeTab === 'enquiries' && <Enquiries />}
        {activeTab === 'clinical' && <Clinical />}
        {activeTab === 'medication' && <Medication />}
        {activeTab === 'careplans' && <CarePlans />}
        {activeTab === 'telehealth' && <Telehealth />}
        {activeTab === 'orders' && <Orders />}
        {activeTab === 'quality' && <Quality />}
        {activeTab === 'roster' && <Roster />}
        {activeTab === 'billing' && <Billing />}
        {activeTab === 'reports' && <Reports />}
        {activeTab === 'network' && <Network />}
        {activeTab === 'alerts' && <Alerts />}
        {activeTab === 'kitchen' && <Kitchen />}
        {activeTab === 'maintenance' && <Maintenance />}
        {activeTab === 'emergency' && <Emergency />}
        {activeTab === 'visitors' && <Visitors />}
        {activeTab === 'wellbeing' && <Wellbeing />}
        {activeTab === 'familyportal' && <FamilyPortal />}
        {activeTab === 'mobileapp' && <MobileCarerApp />}
        {activeTab === 'hr' && <HRModule />}
        {activeTab === 'workforce' && <EnterpriseRoster />}
        {activeTab === 'settings' && <Settings />}
      </div>
    </div>
  );
};

// App with Provider
const App = () => {
  return (
    <AppProvider>
      <ErrorBoundary>
        <LoadingProvider>
          <ToastProvider>
            <ConfirmProvider>
              <OfflineIndicator />
              <MedFastCRMPro />
            </ConfirmProvider>
          </ToastProvider>
        </LoadingProvider>
      </ErrorBoundary>
    </AppProvider>
  );
};

export default App;